import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Invite } from '@models/Core/Invite';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthenticationRequestService } from '@services/auth/authentication.request.service';
import { AuthenticationService } from '@services/auth/authentication.service';
import { BusService } from '@services/bus.service';
import { EventsService } from '@services/events.service';
import { UserInviteService } from '@services/users/invite.service';

import { TermsModalComponent } from '../../misc/terms-modal/terms-modal.component';

@Component({
  selector: 'app-account-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
})
export class SignupComponent implements OnInit, OnDestroy {
  submitted = false;
  errors = {};
  pending = false;
  inviteToken: string;
  invite: Invite = undefined;

  signupForm = new UntypedFormGroup({
    firstName: new UntypedFormControl('', [Validators.required]),
    name: new UntypedFormControl('', [Validators.required]),
    email: new UntypedFormControl('', [Validators.email, Validators.required]),
    password: new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(8),
    ]),
    confirmPassword: new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(8),
      control =>
        control?.value === this.signupForm?.value.password
          ? null
          : { confirmPassword: true },
    ]),
    acceptedTerms: new UntypedFormControl(false, [Validators.requiredTrue]),
  });

  constructor(
    private router: Router,
    private bus: BusService,
    private events: EventsService,
    private auth: AuthenticationRequestService,
    private modalService: NgbModal,
    private authService: AuthenticationService,
    private inviteService: UserInviteService,
    private route: ActivatedRoute,
  ) {}

  get firstName() {
    return this.signupForm.get('firstName');
  }
  get name() {
    return this.signupForm.get('name');
  }
  get email() {
    return this.signupForm.get('email');
  }
  get password() {
    return this.signupForm.get('password');
  }
  get confirmPassword() {
    return this.signupForm.get('confirmPassword');
  }
  get acceptedTerms() {
    return this.signupForm.get('acceptedTerms');
  }

  submitSignup() {
    if (this.signupForm.valid) {
      const model: any = {
        first_name: this.firstName.value,
        name: this.name.value,
        email: this.email.value.trim(),
        password: this.password.value,
      };

      if (this.inviteToken) {
        model.inviteToken = this.inviteToken;
      }

      this.bus.publish(this.events.requested.action.user.create, model);
      this.pending = true;
    } else {
      this.submitted = true;
    }
  }

  openTermsModal() {
    this.modalService.open(TermsModalComponent, {
      size: 'lg',
      windowClass: 'big-modal',
    });
  }

  signupSuccess(response) {
    if (response.verified) {
      this.router.navigate(['/login'], {
        queryParams: {
          email: this.email.value.trim(),
          justSignedUp: 'true',
        },
      });
    } else {
      this.router.navigate(['/signed-up'], {
        queryParams: {
          email: this.email.value.trim(),
        },
      });
    }
    this.pending = false;
  }

  signupFailure(errors) {
    this.errors = errors.errors;
    this.pending = false;
  }

  subscribe() {
    this.bus.subscribe(
      this.events.received.action.user.create.success,
      this.signupSuccess.bind(this),
    );
    this.bus.subscribe(
      this.events.received.action.user.create.failure,
      this.signupFailure.bind(this),
    );
  }

  unsubscribe() {
    this.bus.unsubscribe(
      this.events.received.action.user.create.success,
      this.signupSuccess.bind(this),
    );
    this.bus.unsubscribe(
      this.events.received.action.user.create.failure,
      this.signupFailure.bind(this),
    );
  }

  ngOnInit() {
    this.subscribe();
    this.route.queryParams.subscribe(q => {
      if (q.token) {
        this.inviteToken = q.token;
        this.inviteService.get<Invite>(q.token).subscribe(invite => {
          this.invite = invite;
          this.signupForm.patchValue({
            firstName: invite.first_name,
            ...invite,
          });
        });
      }
    });
  }

  ngOnDestroy() {
    this.unsubscribe();
  }
}
